<script setup>
// Wiersz ekranu TV: miejsce (medale 1–3), numer, nazwisko + imię, kategoria z miejscem, czas.
// Wymiary w em od wysokości wiersza (--tv-row ustawia TvResultsView), więc rosną razem z ekranem.
import { computed, defineProps } from 'vue'
import { nameParts } from '../../utils/tv-name.js'
import { shortPoint } from '../../utils/tv-layout.js'

const props = defineProps({
  item: { type: Object, required: true },
  surnameFirst: { type: Boolean, default: true },
  catMode: { type: String, default: 'category' }, // 'category' | 'gender' | 'none'
  hasPoints: { type: Boolean, default: false },
  phase: { type: String, default: null },
  fresh: { type: Boolean, default: false },
  finishClock: { type: Boolean, default: false } // „Właśnie na mecie”: godzina mety zamiast kategorii
})

const row = computed(() => props.item.row || null)
const name = computed(() => nameParts(row.value?.name, props.surnameFirst))
const foreign = computed(() => !!row.value?.country && row.value.country !== 'POL')
const medal = computed(() => (row.value?.place && row.value.place <= 3 ? `tvr__place--${row.value.place}` : ''))

// Litera płci w kolorze, gdy nazwa kategorii od niej się zaczyna („K40”, „M20”)
const catLetter = computed(() => {
  const r = row.value
  return r && r.gender && r.categoryName && r.categoryName.charAt(0).toUpperCase() === r.gender ? r.gender : ''
})
const catRest = computed(() => (catLetter.value ? row.value.categoryName.slice(1) : row.value?.categoryName || ''))

const timeText = computed(() => {
  const r = row.value
  if (!r) return ''
  if (r.group === 'finished') return props.hasPoints ? `${r.points ?? 0} pkt` : r.timeText
  if (r.group === 'dnf') return r.lastPoint ? `po: ${shortPoint(r.lastPoint)}` : ''
  if (props.phase === 'before') return r.startClock ? r.startClock.slice(0, 5) : ''
  if (props.phase === 'ended') return 'brak mety'
  return r.lastPoint || 'na trasie'
})
const timeKind = computed(() => {
  const g = row.value?.group
  if (g === 'finished') return 'is-time'
  if (g === 'course' && props.phase === 'live') return 'is-course'
  return 'is-note'
})
</script>

<template>
  <li v-if="item.spacer" class="tvr tvr--spacer" aria-hidden="true" />
  <li v-else-if="item.section" class="tvr tvr--section">{{ item.section }}</li>
  <li v-else-if="item.note" class="tvr tvr--note">{{ item.note }}</li>
  <li v-else class="tvr" :class="{ 'is-fresh': fresh }">
    <span class="tvr__place" :class="medal">
      <span v-if="row.place" class="tvr__num" :aria-label="`${row.place}. miejsce`">{{ row.place }}</span>
      <span v-else-if="row.group === 'dnf'" class="tvr__tag tvr__tag--dnf">DNF</span>
      <span v-else-if="row.outOfCompetition" class="tvr__tag" title="Poza klasyfikacją">p.k.</span>
      <span v-else class="tvr__dash" aria-hidden="true">–</span>
    </span>
    <span class="tvr__bib">{{ row.number }}</span>
    <span class="tvr__name">
      <span v-if="foreign" class="tvr__cc" :title="`Kraj: ${row.country}`">{{ row.country }}</span>
      <span class="tvr__text"><strong>{{ name.lead }}</strong><template v-if="name.rest">{{ ' ' + name.rest }}</template></span>
    </span>
    <span v-if="finishClock" class="tvr__cat tvr__cat--clock">{{ row.finishClock.slice(0, 5) }}</span>
    <span v-else-if="catMode === 'category'" class="tvr__cat">
      <span v-if="catLetter" :class="`tvr__g tvr__g--${catLetter}`">{{ catLetter }}</span>{{ catRest }}<strong v-if="row.categoryPlace">{{ ' ' + row.categoryPlace + '.' }}</strong>
    </span>
    <span v-else-if="catMode === 'gender'" class="tvr__cat">
      <template v-if="row.gender"><span :class="`tvr__g tvr__g--${row.gender}`">{{ row.gender }}</span><strong v-if="row.genderPlace">{{ ' ' + row.genderPlace + '.' }}</strong></template>
    </span>
    <span class="tvr__time" :class="timeKind">{{ timeText }}</span>
  </li>
</template>

<style scoped>
/* Kolory tylko z palety (--sledz-*); color-mix z zapasem w deklaracji przed nim (starsze telewizory). */
.tvr {
  height: var(--tv-row);
  padding: 0 0.6em;
  display: flex;
  align-items: center;
  gap: 0.47em;
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: calc(var(--tv-row) * 0.5);
  line-height: 1.1;
  color: var(--sledz-text);
  white-space: nowrap;
}
.tvr:nth-child(even) {
  background: var(--sledz-surface-alt);
}
.tvr.is-fresh {
  background: var(--sledz-highlight);
}
.tvr--spacer {
  border-bottom-color: transparent;
}
.tvr--section {
  background: var(--sledz-border-soft) !important;
  border-bottom: 1px solid var(--sledz-border);
  font-size: calc(var(--tv-row) * 0.4);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sledz-text-secondary);
}
.tvr--note {
  justify-content: center;
  font-size: calc(var(--tv-row) * 0.42);
  color: var(--sledz-text-muted);
  border-bottom: none;
}
.tvr__place {
  flex: none;
  width: 2.15em;
  display: flex;
  justify-content: center;
}
.tvr__num {
  font-size: 1.06em;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}
.tvr__place--1 .tvr__num,
.tvr__place--2 .tvr__num,
.tvr__place--3 .tvr__num {
  width: 1.55em;
  height: 1.55em;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9em;
  color: var(--sledz-brand-ink);
  box-shadow: inset 0 -2px 0 color-mix(in srgb, var(--sledz-brand-ink) 12%, transparent);
}
.tvr__place--1 .tvr__num {
  background: var(--sledz-brand);
}
.tvr__place--2 .tvr__num {
  background: var(--sledz-medal-silver);
}
.tvr__place--3 .tvr__num {
  background: var(--sledz-medal-bronze);
}
.tvr__tag {
  padding: 0.06em 0.3em;
  border-radius: 0.35em;
  background: var(--sledz-border-soft);
  color: var(--sledz-text-secondary);
  font-size: 0.72em;
  font-weight: 800;
}
.tvr__tag--dnf {
  background: var(--sledz-border-soft);
  background: color-mix(in srgb, var(--sledz-live) 14%, var(--sledz-surface));
  color: var(--sledz-live-strong);
}
.tvr__dash {
  color: var(--sledz-text-muted);
}
.tvr__bib {
  flex: none;
  min-width: 2.6em;
  padding: 0.12em 0.3em;
  border-radius: 0.35em;
  background: var(--sledz-border-soft);
  color: var(--sledz-text-secondary);
  text-align: center;
  font-size: 0.82em;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.tvr__name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.35em;
}
.tvr__cc {
  flex: none;
  padding: 0.08em 0.28em;
  border-radius: 0.3em;
  border: 1px solid var(--sledz-border);
  color: var(--sledz-text-secondary);
  font-size: 0.66em;
  font-weight: 800;
  letter-spacing: 0.03em;
}
.tvr__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--sledz-text-secondary);
}
.tvr__text strong {
  font-size: 1.12em;
  font-weight: 800;
  color: var(--sledz-text);
}
.tvr__cat {
  flex: none;
  width: 4.4em; /* „M30 12.” — 3,87 em wiersza; nagłówek kolumny ma tę samą szerokość */
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.88em;
  color: var(--sledz-text-secondary);
}
.tvr__cat strong {
  color: var(--sledz-text);
}
.tvr__cat--clock {
  font-variant-numeric: tabular-nums;
}
.tvr__g {
  font-weight: 800;
}
.tvr__g--K {
  color: var(--sledz-gender-k);
}
.tvr__g--M {
  color: var(--sledz-gender-m);
}
.tvr__time {
  flex: none;
  width: 4.9em;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
.tvr__time.is-time {
  font-size: 1.12em;
  width: 4.4em;
  font-weight: 800;
}
.tvr__time.is-course {
  font-size: 0.88em;
  width: 5.6em;
  font-style: italic;
  color: var(--sledz-text-secondary);
}
.tvr__time.is-note {
  font-size: 0.88em;
  width: 5.6em;
  color: var(--sledz-text-muted);
}
</style>
